import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { DataGrid, GridDeleteIcon } from "@mui/x-data-grid";
import { Edit } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import {
  deleteSetaTemplate,
  fetchAllSetaTemplates,
} from "../../slices/setaTemplateSlice";
import CButton from "../../components/CButton";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import { ReactComponent as EditIcon } from "../../assets/images/edit-icon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/trash-icon.svg";
import CDataGrid from "../../components/CDataGrid";
import CTextField from "../../components/CTextField";
import CDialog from "../../components/CDialog";
import { ReactComponent as DownloadIcon } from "../../assets/images/download-icon.svg";


const SetaSheet = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const { templates } = useSelector((state) => state.setaTemplate);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [dialogState, setDialogState] = useState({ id: null, show: false });
  const [open, setOpen] = useState(false);
  // Fetch SETA templates on component mount
  useEffect(() => {
    dispatch(fetchAllSetaTemplates());
  }, [dispatch]);

  // Format template data for DataGrid
  useEffect(() => {
    if (templates && Array.isArray(templates)) {
      const formattedTemplates = templates.map((template) => ({
        id: template.id,
        seta_name: template.seta_name || "-",
        file_path: template.file_path || "",
      }));
      setFilteredTemplates(formattedTemplates);
    }
  }, [templates]);

  // Handle delete confirmation
  const handleDelete = async () => {
    await dispatch(deleteSetaTemplate(dialogState.id));
    setDialogState({ id: null, show: false });
    setOpen(false);
  };

  // Open confirmation dialog
  const openDialog = (id) => {
    setDialogState({ id, show: true });
  };

  const columns = [
    // { field: "id", headerName: "ID", flex: 1 },
    {
      field: "employee_id",
      headerName: "Employee ID",
      minWidth: 150,
    },
    {
      field: "employee_name",
      headerName: "Employee Name",
      minWidth:200,
    },
    {
      field: "personnel_no",
      headerName: "Personnel No.",
      minWidth: 150,
    },
    {
      field: "email_address",
      headerName: "Email address",
      minWidth:250,
    },
    {
      field: "ofo_code",
      headerName: "OFO Code",
      minWidth: 120,
    },
     {
      field: "employee_job_title",
      headerName: "Employee Job Title",
      minWidth: 180,
    },
     {
      field: "employment_type",
      headerName: "Employment Type",
       minWidth: 180,
    },
     {
      field: "gender",
      headerName: "Gender",
      minWidth:120,
    },
    {
      field: "file_path",
      headerName: "Download File",
      minWidth: 150,
      renderCell: (params) =>
        params.value && params.value !== "manual_input" ? (
          <a
            href={params.value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            Download
          </a>
        ) : (
          "No File"
        ),
    },
    {
      field: "actions",
      headerName: "Actions",
       minWidth:100,
      sortable: false,
      renderCell: (params) => (
        <div className="flex gap-3 items-center h-full">
          <CButton
            tooltip="Edit"
            tooltipPosition={"top"}
            onClick={() => navigate(`/create-seta-template/${params.row.id}`)}
            className="!min-w-0 bg-blue-100 text-blue-600 hover:bg-blue-200 2xl:!h-[28px] 2xl:!w-[28px] !h-[24px] !w-[24px] !p-0 !rounded-[5px] inline-flex items-center justify-center"
          >
            <EditIcon className="!fill-blue-500 !text-blue-500"/>
          </CButton>
          <CButton
            tooltip="Delete"
            tooltipPosition={"top"}
            onClick={() => setDialogState({ id: params.row.id, show: true })}
            className="!min-w-0 bg-red-100 text-red-600 hover:bg-red-200 2xl:!h-[28px] 2xl:!w-[28px] !h-[24px] !w-[24px] !p-0 !rounded-[5px] inline-flex items-center justify-center"
          >
            <DeleteIcon />
          </CButton>
        </div>
      ),
    },
  ];

  return (
    <div>
      <h2 className="2xl:text-3xl 2xl:leading-3xl text-2xl leading-2xl text-blue-500 font-semibold 2xl:mb-4 md:mb-3 mb-4">
        SETA Templates
      </h2>
      <div className="bg-white border border-solid border-blue-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between p-3 g-2">
          <CTextField
            name="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            startIcon={<SearchIcon className="text-blue-300" />}
            placeholder={"Search"}
            className="!mt-0 w-full 2xl:max-w-[400px] xl:max-w-[300px] lg:max-w-[250px] max-w-[200px]"
          />
          <div className="flex gap-2">
            <CButton className="bg-blue-500 text-white  hover:bg-blue-700 border border-solid border-blue-500 hover:border-blue-700 rounded-lg p-0 ">
              <DownloadIcon className="fill-white"/> Download Training Template
            </CButton>
            <CButton
              className="bg-white text-blue-500 hover:text-white hover:bg-blue-500 border border-solid border-blue-500 hover:border-blue-500 rounded-lg p-0 "
            >
              Populate the SETA Sheet
            </CButton>
            <CButton
              className="bg-white text-blue-500 hover:text-white hover:bg-blue-500 border border-solid border-blue-500 hover:border-blue-500 rounded-lg p-0 "
            >
              Export SETA Sheet
            </CButton>
          </div>
        </div>

        <div className="2xl:h-[calc(100vh-240px)] h-[calc(100vh-212px)] w-full overflow-auto">
          <CDataGrid
            searchQuery={searchQuery}
            rows={filteredTemplates}
            idKey={"id"}
            columns={columns}
            pageSize={10}
            getRowId={(row) => row.id}
            className="bg-white shadow-md rounded-lg"
          />
        </div>
      </div>

      {/* Confirmation Dialog */}
      {dialogState.show && (
        <CDialog
          open={dialogState.show}
          onClose={() => setDialogState({ id: null, show: false })}
          className="deleteDialog bg-[#95AEBB80]"
        >
          <div className="p-5">
            <p className="text-2xl leading-2xl text-grey-700 font-semibold text-center">
              Are you sure you want to delete this template?
            </p>
            <div className="flex justify-center mt-8 gap-3">
              <CButton
                onClick={handleDelete}
                className="bg-blue-500 hover:bg-blue-700 min-w-[80px] text-white border border-solid border-blue-500 hover:border-blue-700"
              >
                Yes
              </CButton>
              <CButton
                onClick={() => setDialogState({ id: null, show: false })}
                className="bg-transparent hover:bg-blue-500 border border-solid border-blue-500 hover:border-blue-500 min-w-[80px] text-blue-500 hover:text-white"
              >
                No
              </CButton>
            </div>
          </div>
        </CDialog>
      )}
    </div>
  );
};

export default SetaSheet;
